// =====================================================
// FeedbackWidget — floating beta feedback collector
// =====================================================
// Always-visible button (bottom-right) that opens a
// feedback panel. On mobile: bottom sheet (avoids
// keyboard clipping). On desktop: right-side sheet.
// Data is inserted into the `beta_feedback` table.

import { useState, useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageSquarePlus, Smile, Meh, Frown, Send, Loader2, CheckCircle2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

type Mood = 'positive' | 'neutral' | 'negative';

const MOOD_OPTIONS: { value: Mood; icon: typeof Smile; label: string }[] = [
  { value: 'positive', icon: Smile, label: 'Good' },
  { value: 'neutral', icon: Meh, label: 'Okay' },
  { value: 'negative', icon: Frown, label: 'Frustrated' },
];

/** Derive a human-friendly page name from the URL path */
function pageName(path: string): string {
  const segment = path.split('/').filter(Boolean)[0] || 'Home';
  return segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
}

const FeedbackWidget = () => {
  const { user } = useAuth();
  const location = useLocation();
  const { toast } = useToast();

  const [open, setOpen] = useState(false);
  const [mood, setMood] = useState<Mood | null>(null);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const resetForm = useCallback(() => {
    setMood(null);
    setMessage('');
    setSubmitting(false);
    setSubmitted(false);
  }, []);

  // Focus trap: keep Tab navigation within the panel when open
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { handleClose(); return; }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const focusable = panelRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  const handleSubmit = useCallback(async () => {
    if (!message.trim() || !user) return;
    setSubmitting(true);

    try {
      const { error } = await (supabase.from as any)('beta_feedback').insert({
        user_id: user.id,
        user_email: user.email ?? null,
        page_path: location.pathname,
        mood: mood,
        message: message.trim(),
      });

      if (error) throw error;

      setSubmitted(true);
      // Auto-close after showing thanks
      setTimeout(() => {
        setOpen(false);
        // Reset after close animation
        setTimeout(resetForm, 350);
      }, 1800);
    } catch (err) {
      console.error('Feedback submit error:', err);
      toast({
        title: 'Could not send feedback',
        description: 'Please try again in a moment.',
      });
      setSubmitting(false);
    }
  }, [message, user, location.pathname, mood, resetForm, toast]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setTimeout(resetForm, 350);
  }, [resetForm]);

  // Only show for authenticated users
  if (!user) return null;

  return (
    <>
      {/* Floating trigger button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-30 flex items-center gap-2 px-4 py-2.5 rounded-full bg-ds-card border border-ds-border shadow-lg hover:border-ds-gold/50 hover:shadow-ds-gold/10 transition-all group"
          aria-label="Send feedback"
        >
          <MessageSquarePlus className="w-4 h-4 text-ds-gold" />
          <span className="text-sm text-ds-gray-2 group-hover:text-ds-gray-1 transition-colors">
            Feedback
          </span>
        </button>
      )}

      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/50 animate-in fade-in-0"
          onClick={handleClose}
        />
      )}

      {/* Feedback panel — bottom sheet on mobile, right panel on desktop */}
      {open && (
        <div
          ref={panelRef}
          className={cn(
            'fixed z-50 bg-ds-card border-ds-border shadow-xl flex flex-col animate-in',
            // Mobile: bottom sheet, max 70% of viewport height
            'inset-x-0 bottom-0 rounded-t-2xl border-t border-x max-h-[70dvh]',
            'slide-in-from-bottom duration-300',
            // Desktop: right side panel
            'sm:inset-x-auto sm:inset-y-0 sm:right-0 sm:rounded-t-none sm:rounded-l-none sm:border-l sm:border-t-0 sm:border-x-0',
            'sm:w-[340px] sm:max-h-none sm:slide-in-from-right sm:slide-in-from-bottom-0'
          )}
        >
          {/* Mobile drag handle */}
          <div className="sm:hidden flex justify-center pt-2 pb-1">
            <div className="w-10 h-1 rounded-full bg-ds-border" />
          </div>

          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute right-3 top-3 sm:top-4 sm:right-4 rounded-sm opacity-70 hover:opacity-100 transition-opacity"
          >
            <X className="h-4 w-4 text-ds-gray-3" />
            <span className="sr-only">Close</span>
          </button>

          {submitted ? (
            /* ── Thank-you state ── */
            <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center p-6">
              <div className="w-14 h-14 rounded-full bg-ds-green/10 flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7 text-ds-green" />
              </div>
              <div>
                <p className="text-lg font-medium text-ds-gray-1">Thanks!</p>
                <p className="text-sm text-ds-gray-3 mt-1">
                  We read every one of these.
                </p>
              </div>
            </div>
          ) : (
            /* ── Form state ── */
            <div className="flex flex-col flex-1 overflow-hidden">
              {/* Scrollable content area */}
              <div className="flex-1 overflow-y-auto px-5 pt-4 sm:pt-6 pb-2">
                {/* Header */}
                <div className="mb-4">
                  <h3 className="text-base font-semibold text-ds-gray-1">
                    Share your thoughts
                  </h3>
                  <p className="text-sm text-ds-gray-3 mt-0.5">
                    Your feedback helps us improve Scholar Vision.
                  </p>
                </div>

                {/* Mood selector */}
                <div className="mb-4">
                  <p className="text-xs text-ds-gray-3 mb-2">
                    How are you feeling? <span className="text-ds-gray-3">(optional)</span>
                  </p>
                  <div className="flex gap-2">
                    {MOOD_OPTIONS.map(({ value, icon: Icon, label }) => (
                      <button
                        key={value}
                        onClick={() => setMood(mood === value ? null : value)}
                        className={cn(
                          'flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm transition-all',
                          mood === value
                            ? value === 'positive'
                              ? 'border-ds-green bg-ds-green/10 text-ds-green'
                              : value === 'neutral'
                                ? 'border-ds-gold bg-ds-gold/10 text-ds-gold'
                                : 'border-red-400 bg-red-400/10 text-red-400'
                            : 'border-ds-border text-ds-gray-3 hover:border-ds-gray-3 hover:text-ds-gray-2'
                        )}
                      >
                        <Icon className="w-4 h-4" />
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Message input */}
                <Textarea
                  placeholder="What's on your mind?"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  className="bg-ds-bg border-ds-border text-ds-gray-1 placeholder:text-ds-gray-3 resize-none w-full"
                  disabled={submitting}
                />

                {/* Page context badge */}
                <p className="text-xs text-ds-gray-3 mt-2">
                  Sharing from: <span className="text-ds-gray-2">{pageName(location.pathname)}</span>
                </p>
              </div>

              {/* Pinned submit button — always visible above keyboard */}
              <div className="flex-shrink-0 px-5 pb-5 sm:pb-6 pt-2 border-t border-ds-border/50">
                <Button
                  onClick={handleSubmit}
                  disabled={!message.trim() || submitting}
                  className="w-full bg-ds-gold hover:bg-ds-gold/90 text-black font-medium"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Send Feedback
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default FeedbackWidget;
